import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useWaProvider } from "../lib/waProvider";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { BellRing, Play, Sparkles } from "lucide-react";
import { AiModelPicker } from "./AiModelPicker";

function WeeklyAiRow() {
  const { label: waLabel } = useWaProvider();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/ai/weekly-report").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const save = async (patch) => {
    try { const { data } = await api.put("/ai/weekly-report", { ...cfg, ...patch }); setCfg((c) => ({ ...c, ...data })); toast.success("Report AI settimanale aggiornato"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!cfg) return null;
  return (
    <div className="rounded-lg border border-indigo-800/40 bg-indigo-500/5 p-3 space-y-2" data-testid="weekly-ai-row">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs"><Switch checked={!!cfg.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="weekly-ai-enabled" /> <Sparkles className="h-3.5 w-3.5 text-indigo-300" /> <b>Report AI nel riepilogo del lunedì</b> (sintesi + 3 consigli pratici)</label>
        <label className="flex items-center gap-2 text-xs"><Switch checked={cfg.include_crm !== false} onCheckedChange={(v) => save({ include_crm: v })} data-testid="weekly-ai-crm" /> usa anche gli scontrini CRM Elastic</label>
        <AiModelPicker value={{ provider: cfg.provider, model: cfg.model }} onChange={(p) => save(p)} testid="weekly-ai-pick" />
      </div>
      <p className="text-[11px] text-zinc-500">Arriva nella campanella, via email e su WhatsApp: nel template {waLabel} "Riepilogo settimanale" aggiungi la variabile <code>{"{{ai_insights}}"}</code>. Il bottone "📊 Riepilogo" qui sotto lo genera subito.</p>
      {cfg.last?.ai_insights && <div className="text-[11px] text-zinc-300 whitespace-pre-line border-t border-indigo-800/30 pt-2" data-testid="weekly-ai-last">🤖 Ultimo ({cfg.last.week}): {cfg.last.ai_insights}</div>}
    </div>
  );
}

const fmtIt = (s) => (s ? s.split("-").reverse().join("/") : "");

// Admin card: KPI drop alert config (threshold %, window) + manual check
export default function KpiAlertCard() {
  const { label: waLabel } = useWaProvider();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [weeklyBusy, setWeeklyBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/stats/evening-digest/run-now");
      toast.success(`Digest inviato: ${data.orders_open} ordini aperti · ${data.reservations_pending} prenotazioni · ${data.messages_unread} messaggi`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDigestBusy(false); }
  };
  const [result, setResult] = useState(null);

  const sendWeekly = async () => {
    setWeeklyBusy(true);
    try {
      const { data } = await api.post("/stats/weekly-summary/run-now");
      toast.success(`Riepilogo ${data.from?.split("-").reverse().join("/")}–${data.to?.split("-").reverse().join("/")} inviato${data.whatsapp ? ` · WhatsApp ${data.whatsapp.state}` : ""}${data.ai_insights ? " · con analisi AI" : ""}`);
      if (data.ai_insights) toast.info(data.ai_insights.slice(0, 300) + (data.ai_insights.length > 300 ? "…" : ""), { duration: 15000 });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setWeeklyBusy(false); }
  };

  useEffect(() => {
    api.get("/settings/kpi-alert").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled: cfg.enabled, threshold_pct: Number(cfg.threshold_pct), window_days: Number(cfg.window_days), notify_email: cfg.notify_email, admin_phone: (cfg.admin_phone || "").trim(), alert_growth: !!cfg.alert_growth };
      await api.put("/settings/kpi-alert", body);
      toast.success("Alert calo KPI salvato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/stats/kpi-alert/run-now");
      setResult(data);
      if (data.notified || data.notified_up) toast.warning(`${data.notified ? "Calo" : "Picco"} rilevato: notifica admin inviata`);
      else if (data.triggered?.length || data.triggered_up?.length) toast.info("Variazione rilevata (notifica già inviata oggi)");
      else toast.success("Nessuna variazione oltre soglia");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRunning(false); }
  };

  return (
    <div className="card-elev p-5 space-y-3" data-testid="kpi-alert-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-amber-300" />
          <h3 className="font-heading font-semibold text-lg">Alert calo KPI</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-zinc-500">{cfg.enabled ? "Attivo" : "Disattivato"}</span>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="kpi-alert-enabled" />
        </div>
      </div>
      <p className="text-xs text-zinc-500">
        Ogni giorno (dopo le 08:00) confronta gli ultimi N giorni completi con gli N precedenti: se fatturato o transazioni calano{cfg.alert_growth ? " (o crescono)" : ""} oltre la soglia, l'admin riceve una notifica interna{cfg.notify_email ? " e un'email" : ""}{cfg.admin_phone ? " e un WhatsApp" : ""}. Ogni lunedì alle 09:00 arriva anche il 📊 riepilogo della settimana e ogni sera alle 21:00 il 🌙 digest dei non gestiti.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Soglia %</label>
          <Input type="number" min={1} max={100} value={cfg.threshold_pct} onChange={(e) => set("threshold_pct", e.target.value)} data-testid="kpi-alert-threshold" className="bg-zinc-950 border-zinc-800 h-9" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Finestra gg</label>
          <Input type="number" min={1} max={90} value={cfg.window_days} onChange={(e) => set("window_days", e.target.value)} data-testid="kpi-alert-window" className="bg-zinc-950 border-zinc-800 h-9" />
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-300 h-9">
          <Switch checked={!!cfg.notify_email} onCheckedChange={(v) => set("notify_email", v)} data-testid="kpi-alert-email" /> Email admin
        </label>
        <label className="flex items-center gap-2 text-xs text-zinc-300 h-9">
          <Switch checked={!!cfg.alert_growth} onCheckedChange={(v) => set("alert_growth", v)} data-testid="kpi-alert-growth" /> 🚀 Anche picchi
        </label>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">WhatsApp admin ({waLabel})</label>
          <Input type="tel" placeholder="+39 3xx xxxxxxx" value={cfg.admin_phone || ""} onChange={(e) => set("admin_phone", e.target.value)} data-testid="kpi-alert-phone" className="bg-zinc-950 border-zinc-800 h-9" />
        </div>
        <div className="flex gap-2 justify-end col-span-2 sm:col-span-3 lg:col-span-1">
          <Button size="sm" onClick={save} disabled={saving} data-testid="kpi-alert-save" className="h-9 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">Salva</Button>
          <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="kpi-alert-run" className="h-9 border-amber-800/50 text-amber-300 hover:bg-amber-500/10">
            <Play className="h-3.5 w-3.5 mr-1" /> Controlla ora
          </Button>
          <Button size="sm" variant="outline" onClick={sendWeekly} disabled={weeklyBusy} data-testid="weekly-summary-run" title="Invia adesso il riepilogo della settimana scorsa (automatico ogni lunedì 09:00)" className="h-9 border-indigo-800/50 text-indigo-300 hover:bg-indigo-500/10">
            📊 Riepilogo
          </Button>
          <Button size="sm" variant="outline" onClick={sendDigest} disabled={digestBusy} data-testid="evening-digest-run" title="Invia adesso il digest degli ordini/prenotazioni/messaggi non gestiti (automatico ogni sera alle 21:00)" className="h-9 border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            🌙 Digest
          </Button>
        </div>
      </div>
      <WeeklyAiRow />
      {result && (
        <div className={`text-xs rounded-lg p-3 border ${result.triggered?.length ? "border-red-800/60 bg-red-500/10 text-red-200" : result.triggered_up?.length ? "border-emerald-700/60 bg-emerald-500/10 text-emerald-200" : "border-zinc-700 bg-zinc-900/60 text-zinc-300"}`} data-testid="kpi-alert-result">
          <div className="font-semibold mb-1">
            {result.triggered?.length
              ? `📉 Calo oltre ${result.threshold_pct}% su: ${result.triggered.map((k) => (k === "revenue" ? "Fatturato" : "Transazioni")).join(", ")}`
              : result.triggered_up?.length
                ? `🚀 Picco oltre ${result.threshold_pct}% su: ${result.triggered_up.map((k) => (k === "revenue" ? "Fatturato" : "Transazioni")).join(", ")}`
                : "✓ Nessuna variazione oltre soglia"}
            {(result.notified || result.notified_up) && " — notifica inviata"}
            {(result.whatsapp || result.whatsapp_up) && ` · WhatsApp: ${(result.whatsapp || result.whatsapp_up).state === "accepted" ? "inviato ✓" : `${(result.whatsapp || result.whatsapp_up).state} (${(result.whatsapp || result.whatsapp_up).reason || ""})`}`}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px] opacity-90">
            <div>Ultimi {result.window_days} gg ({fmtIt(result.current.from)}–{fmtIt(result.current.to)}): € {result.current.revenue.toFixed(2)} · {result.current.tx_count} tx</div>
            <div>Precedenti ({fmtIt(result.previous.from)}–{fmtIt(result.previous.to)}): € {result.previous.revenue.toFixed(2)} · {result.previous.tx_count} tx</div>
            <div>Δ Fatturato: {result.drops.revenue > 0 ? `−${result.drops.revenue}%` : result.growths?.revenue > 0 ? `+${result.growths.revenue}%` : "stabile"}</div>
            <div>Δ Transazioni: {result.drops.tx_count > 0 ? `−${result.drops.tx_count}%` : result.growths?.tx_count > 0 ? `+${result.growths.tx_count}%` : "stabile"}</div>
          </div>
        </div>
      )}
      {cfg.last_alert && !result && (
        <div className="text-[11px] text-zinc-500" data-testid="kpi-alert-last">
          Ultimo alert: {cfg.last_alert.title} · {fmtIt((cfg.last_alert.created_at || "").slice(0, 10))}
        </div>
      )}
    </div>
  );
}
